import { Trans, t } from '@lingui/macro';
import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import { AlertTriangle, Home, RefreshCw, ArrowLeft, Bug, WifiOff, ShieldX, FileQuestion } from 'lucide-react';
import { Button } from '@shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@shared/ui/card';

interface ErrorDetails {
    title: string;
    description: string;
    icon: React.ReactNode;
    showStack?: boolean;
}

/**
 * Premium Route Error Boundary Component
 * 
 * Provides a beautiful, user-friendly error page for React Router errors.
 * Features:
 * - Auto-detects error type (404, 403, 500, network errors, etc.)
 * - Beautiful gradient backgrounds matching brand colors
 * - Animated icons and smooth transitions
 * - Helpful action buttons (retry, go home, go back)
 * - Development mode error details
 */
export const RouteErrorBoundary = () => {
    const error = useRouteError();
    const navigate = useNavigate();

    // Extract error information based on error type
    const getErrorDetails = (): ErrorDetails => {
        // If used as a direct element (catch-all route), show 404
        if (!error) {
            return {
                title: t({
                    id: 'shared.components.shared.RouteErrorBoundary.title-page-not-found',
                    message: 'Page Not Found',
                }),
                description: t({
                    id: 'shared.components.shared.RouteErrorBoundary.description-the-page-you-re',
                    message: 'The page you\'re looking for doesn\'t exist or has been moved.',
                }),
                icon: <FileQuestion className="h-12 w-12" />,
            };
        }

        if (isRouteErrorResponse(error)) {
            switch (error.status) {
                case 404:
                    return {
                        title: t({
                            id: 'shared.components.shared.RouteErrorBoundary.title-page-not-found_1',
                            message: 'Page Not Found',
                        }),
                        description: t({
                            id: 'shared.components.shared.RouteErrorBoundary.description-the-page-you-re_1',
                            message: 'The page you\'re looking for doesn\'t exist or has been moved.',
                        }),
                        icon: <FileQuestion className="h-12 w-12" />,
                    };
                case 403:
                    return {
                        title: t({
                            id: 'shared.components.shared.RouteErrorBoundary.title-access-denied',
                            message: 'Access Denied',
                        }),
                        description: t({
                            id: 'shared.components.shared.RouteErrorBoundary.description-you-don-t-have',
                            message: 'You don\'t have permission to access this page.',
                        }),
                        icon: <ShieldX className="h-12 w-12" />,
                    };
                case 401:
                    return {
                        title: t({
                            id: 'shared.components.shared.RouteErrorBoundary.title-authentication-required',
                            message: 'Authentication Required',
                        }),
                        description: t({
                            id: 'shared.components.shared.RouteErrorBoundary.description-please-sign-in-to',
                            message: 'Please sign in to access this page.',
                        }),
                        icon: <ShieldX className="h-12 w-12" />,
                    };
                case 500:
                    return {
                        title: t({
                            id: 'shared.components.shared.RouteErrorBoundary.title-server-error',
                            message: 'Server Error',
                        }),
                        description: t({
                            id: 'shared.components.shared.RouteErrorBoundary.description-something-went-wrong-on',
                            message: 'Something went wrong on our end. Our team has been notified.',
                        }),
                        icon: <Bug className="h-12 w-12" />,
                        showStack: true,
                    };
                case 503:
                    return {
                        title: t({
                            id: 'shared.components.shared.RouteErrorBoundary.title-service-unavailable',
                            message: 'Service Unavailable',
                        }),
                        description: t({
                            id: 'shared.components.shared.RouteErrorBoundary.description-the-service-is-temporarily',
                            message: 'The service is temporarily unavailable. Please try again later.',
                        }),
                        icon: <WifiOff className="h-12 w-12" />,
                    };
                default:
                    return {
                        title: t({
                            id: 'shared.components.shared.RouteErrorBoundary.error-status',
                            message: 'Error {status}',
                        }, {
                            status: error.status,
                        }),
                        description: error.statusText || 'An unexpected error occurred.',
                        icon: <AlertTriangle className="h-12 w-12" />,
                        showStack: true,
                    };
            }
        }

        // Check for network errors
        if (error instanceof TypeError && error.message.includes('fetch')) {
            return {
                title: t({
                    id: 'shared.components.shared.RouteErrorBoundary.title-connection-error',
                    message: 'Connection Error',
                }),
                description: t({
                    id: 'shared.components.shared.RouteErrorBoundary.description-unable-to-connect-to',
                    message: 'Unable to connect to the server. Please check your internet connection.',
                }),
                icon: <WifiOff className="h-12 w-12" />,
            };
        }

        // Generic error handling
        if (error instanceof Error) {
            return {
                title: t({
                    id: 'shared.components.shared.RouteErrorBoundary.title-something-went-wrong',
                    message: 'Something Went Wrong',
                }),
                description: error.message || 'An unexpected error occurred.',
                icon: <AlertTriangle className="h-12 w-12" />,
                showStack: true,
            };
        }

        return {
            title: t({
                id: 'shared.components.shared.RouteErrorBoundary.title-unexpected-error',
                message: 'Unexpected Error',
            }),
            description: t({
                id: 'shared.components.shared.RouteErrorBoundary.description-an-unexpected-error-occurred',
                message: 'An unexpected error occurred. Please try again.',
            }),
            icon: <AlertTriangle className="h-12 w-12" />,
            showStack: true,
        };
    };

    const errorDetails = getErrorDetails();
    const isDev = import.meta.env.DEV;

    const handleRetry = () => {
        window.location.reload();
    };

    const handleGoHome = () => {
        navigate('/', { replace: true });
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-muted/30 relative overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse" />
                <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-destructive/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
                <div className="absolute top-1/2 left-0 w-64 h-64 bg-accent/5 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0.5s' }} />
            </div>

            {/* Grid pattern overlay */}
            <div
                className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03]"
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}
            />

            <Card className="w-full max-w-lg relative z-10 elevated-3 animate-fade-in-up border-border/50 backdrop-blur-sm bg-card/95">
                <CardHeader className="text-center pb-2">
                    {/* Animated Icon Container */}
                    <div className="mx-auto mb-6 relative">
                        {/* Outer ring animation */}
                        <div className="absolute inset-0 rounded-full bg-destructive/10 animate-ping" style={{ animationDuration: '3s' }} />
                        {/* Icon container */}
                        <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-destructive/10 via-destructive/5 to-transparent border border-destructive/20 mx-auto">
                            <div className="text-destructive animate-scale-in">
                                {errorDetails.icon}
                            </div>
                        </div>
                    </div>

                    <CardTitle className="text-2xl md:text-3xl font-bold tracking-tight">
                        <Trans id='shared.components.shared.RouteErrorBoundary.error-title'>
                            {errorDetails.title}
                        </Trans>
                    </CardTitle>

                    <CardDescription className="text-base mt-3 max-w-md mx-auto leading-relaxed">
                        <Trans id='shared.components.shared.RouteErrorBoundary.error-description'>
                            {errorDetails.description}
                        </Trans>
                    </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6 pt-4">
                    {/* Error code badge for route errors */}
                    {isRouteErrorResponse(error) && (
                        <div className="flex justify-center">
                            <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-mono font-medium bg-muted text-muted-foreground border border-border">
                                <Trans id='shared.components.shared.RouteErrorBoundary.error-code'>
                                    Error Code: {error.status}
                                </Trans>
                            </span>
                        </div>
                    )}

                    {/* Development mode error details */}
                    {isDev && errorDetails.showStack && error instanceof Error && error.stack && (
                        <details className="group">
                            <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2">
                                <Bug className="h-4 w-4" />
                                <Trans id='shared.components.shared.RouteErrorBoundary.error-details-dev'>
                                    Error Details (Development)
                                </Trans>
                            </summary>
                            <pre className="mt-3 text-xs bg-muted/50 p-4 rounded-lg overflow-auto max-h-48 font-mono border border-border/50">
                                {error.message}
                                {error.stack && `\n\n${error.stack}`}
                            </pre>
                        </details>
                    )}

                    {/* Action buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button
                            onClick={handleGoBack}
                            variant="outline"
                            className="flex items-center gap-2 transition-all duration-200 hover:bg-muted"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            <Trans id='shared.components.shared.RouteErrorBoundary.go-back'>
                                Go Back
                            </Trans>
                        </Button>

                        <Button
                            onClick={handleRetry}
                            variant="outline"
                            className="flex items-center gap-2 transition-all duration-200 hover:bg-muted"
                        >
                            <RefreshCw className="h-4 w-4" />
                            <Trans id='shared.components.shared.RouteErrorBoundary.try-again'>
                                Try Again
                            </Trans>
                        </Button>

                        <Button
                            onClick={handleGoHome}
                            className="flex items-center gap-2 bg-primary hover:bg-primary/90 transition-all duration-200"
                        >
                            <Home className="h-4 w-4" />
                            <Trans id='shared.components.shared.RouteErrorBoundary.go-home'>
                                Go Home
                            </Trans>
                        </Button>
                    </div>

                    {/* Support message */}
                    <p className="text-xs text-center text-muted-foreground pt-2">
                        <Trans id='shared.components.shared.RouteErrorBoundary.support-message'>
                            If this problem persists, please contact our support team.
                        </Trans>
                    </p>
                </CardContent>
            </Card>
        </div>
    );
};

export default RouteErrorBoundary;
